import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  TransferWithEvidenceService,
  TransferEvidenceUploadEntity,
  UploadEvidenceEntity,
} from 'services/openapi';
import { DEFAULT_EVIDENCE_EXTENSION } from 'features/bidvTransferWithEvidence/shared/configs';

const UPLOAD_EVIDENCE_FAILED_MESSAGE =
  'Upload chứng từ thất bại, vui lòng thử lại';

const getUploadEvidenceErrorMessage = (error: unknown) => {
  if (error instanceof Error && error.message.startsWith('Upload chứng từ')) {
    return error.message;
  }

  return UPLOAD_EVIDENCE_FAILED_MESSAGE;
};

const uploadFile = async (sasUrl: string, file: File): Promise<void> => {
  const res = await fetch(sasUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': file.type,
      'x-ms-blob-type': 'BlockBlob',
    },
    body: file,
  });

  if (!res.ok) {
    const statusText = res.statusText ? ` ${res.statusText}` : '';
    throw new Error(
      `${UPLOAD_EVIDENCE_FAILED_MESSAGE} (${res.status}${statusText})`,
    );
  }
};

export const useUploadTransferEvidenceMutation = () => {
  const { isLoading, error, data, isError, isSuccess, mutate, mutateAsync } =
    useMutation<string[], Error, File[]>({
      mutationFn: async (files: File[]) => {
        const uploadEvidence: UploadEvidenceEntity =
          await TransferWithEvidenceService.transferWithEvidenceControllerUploadEvidence(
            {
              evidenceExtension: DEFAULT_EVIDENCE_EXTENSION,
              evidenceCount: files.length,
            },
          );

        const uploadItems: TransferEvidenceUploadEntity[] =
          uploadEvidence.data ?? [];

        if (uploadItems.length < files.length) {
          throw new Error('Không đủ upload URL cho chứng từ');
        }

        try {
          await Promise.all(
            files.map((file, index) =>
              uploadFile(uploadItems[index].sasUrl, file),
            ),
          );
        } catch (error) {
          toast.error(getUploadEvidenceErrorMessage(error));
          throw error;
        }

        return uploadItems
          .slice(0, files.length)
          .map((item) => item.evidenceUrl);
      },
    });

  return { isLoading, isSuccess, error, data, isError, mutate, mutateAsync };
};
